import {SendOutlined} from "@ant-design/icons";
import {Button, ButtonProps, Space, Tooltip, message} from "antd";
import React, {FC, useState} from "react";
import i18n from "../../../../../services/i18n";
import {
    GetBookingByIdQuery,
    GetBookingByIdWithCourseLessonBookingsQuery,
    MailDataType,
    SendInvoicesToSelectionOfBookingInput,
    TemplateType,
    EditTemplateInput,
    useSendBookingConfirmationMutation,
    useSendInvoicesToSelectionOfBookingsMutation,
} from "../../../../../generated/graphql";
import {handleError, KnownError} from "../../../../../errors/handleErrors";
import {SentMailsResult} from "../../../../../../server/mailjet.types";
import {isDefined} from "../../../../../helpers/typeScriptHelpers";
import EmailTemplatePicker, {SelectionType} from "../../../../templates/EmailRefactoring/EmailTemplatePicker";
import MailTemplateEditor from "../../../../templates/MailTemplateEditor/MailTemplateEditor";

const buttonProps: ButtonProps = {
    size: "small",
    type: "text",
    block: true,
    style: {textAlign: "left"},
};

type BookingType = GetBookingByIdQuery["bookingById"] | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type MenuItemProps = {
    // booking: GetBookingByIdQuery["bookingById"];
    booking: BookingType;
    booker: string;
    isReversal?: boolean;
};

// SEND INVOICE OR REVERSAL INVOICE ----------------------------------------------------------------------------------

export const SendInvoice: FC<MenuItemProps> = ({booking, booker, isReversal = false}) => {
    const [sending, setSending] = useState(false);
    const [showEditor, setShowEditor] = useState(false);
    const [sendInvoice] = useSendInvoicesToSelectionOfBookingsMutation({
        // Versandhistorie (CommunicationHistoryBooking) sofort nach dem Versand aktualisieren
        refetchQueries: ["GetEmailNotificationsByBookingId"],
        awaitRefetchQueries: true,
    });

    const bookerEmail = booking?.attendee?.booker?.email ?? "";

    const sendInvoiceToBooker = async (bookingId?: string, isReversalInvoice?: boolean, editedTemplateInput?: EditTemplateInput) => {
        if (!bookingId) {
            return;
        }
        try {
            setSending(true);
            message.loading({
                key: "sendingInvoice",
                content: "Rechnung wird versendet...",
                duration: 0,
            });

            const sendInvoicesToSelectionOfBookingsInput: SendInvoicesToSelectionOfBookingInput = {
                bookingIds: [bookingId],
                reversal: isReversalInvoice,
                editedTemplateInput,
            };

            await sendInvoice({
                variables: {sendInvoicesToSelectionOfBookingsInput},
            });
            if (isReversalInvoice) {
                message.success({
                    key: "sendingInvoice",
                    content: i18n.containers.invoice.InvoiceDetails.successSendingReversalInvoice(),
                });
            } else {
                message.success({
                    key: "sendingInvoice",
                    content: i18n.containers.invoice.InvoiceDetails.successSendingInvoice(),
                });
            }
            setSending(false);
        } catch (e) {
            console.log(e);
            message.error({
                key: "sendingInvoice",
                content: i18n.containers.invoice.InvoiceDetails.errorSendingInvoice(),
            });
            setSending(false);
        }
    };

    const handleConfirmAction = async (confirmed: boolean, editedTemplateInput?: EditTemplateInput) => {
        setShowEditor(false);
        if (confirmed) {
            await sendInvoiceToBooker(booking.id, isReversal, editedTemplateInput);
        }
    };

    type DataType = {
        bookerHasNoMail: string;
        buttonLabel: string;
        templateType: TemplateType;
    };

    let text: DataType = {
        bookerHasNoMail: i18n.containers.invoice.InvoiceDetails.bookerHasNoEmail(),
        buttonLabel: i18n.containers.invoice.InvoiceDetails.resendInvoice(),
        templateType: TemplateType.Invoice,
    };

    if (isReversal) {
        text = {
            bookerHasNoMail: i18n.containers.invoice.InvoiceDetails.bookerHasNoEmail(),
            buttonLabel: i18n.containers.invoice.InvoiceDetails.resendReversalInvoice(),
            templateType: TemplateType.InvoiceReversal,
        };
    }

    return (
        <>
            <MailTemplateEditor
                asModal
                bookingId={booking.id}
                open={showEditor}
                setVisibility={setShowEditor}
                templateType={text.templateType}
                useCase="SENDMAIL"
                confirmAction={handleConfirmAction}
                receivers={[bookerEmail]}
                hideNotSendButton
            />
            <Tooltip title={Boolean(booking?.attendee?.booker?.email) === false ? text.bookerHasNoMail : ""}>
                <Button {...buttonProps} icon={<SendOutlined />} loading={sending} disabled={!booking.id || !booking.attendee?.booker?.email} onClick={() => setShowEditor(true)}>
                    {text.buttonLabel}
                </Button>
            </Tooltip>
        </>
    );
};

// SEND BOOKING CONFIRMATION ----------------------------------------------------------------------------------------

export const SendBookingConfirmation: FC<MenuItemProps> = ({booking, booker}) => {
    const [showEditor, setShowEditor] = useState(false);
    const isWaitlist = booking.bookingType === "Waitlist";
    const bookingOrWaitlistConfirmationText = isWaitlist ? "Wartelistenbestätigung" : "Buchungsbestätigung";
    const bookerMailIsMissing = !isDefined(booking.attendee?.booker?.email);
    const bookerEmail = booking?.attendee?.booker?.email ?? "";

    const [sendBookingConfirmation, {loading: sendBookingConfirmationLoading}] = useSendBookingConfirmationMutation({
        // Versandhistorie (CommunicationHistoryBooking) sofort nach dem Versand aktualisieren
        refetchQueries: ["GetEmailNotificationsByBookingId"],
        awaitRefetchQueries: true,
    });

    const resendBookingConfirmation = async (editedTemplateInput?: EditTemplateInput) => {
        const bookingId = booking.id;

        const knownErrors: Array<KnownError> = [
            {
                key: "No booker email found",
                title: "Keine E-Mail-Adresse gefunden",
                msg: "Es konnte keine E-Mail-Adresse für den Bucher gefunden werden.",
            },
            {
                key: "default",
                title: "Fehler",
                msg: i18n.containers.bookings.BookingSummary.sendBookingConfirmation.messages.errorText({
                    bookingOrWaitlistConfirmationText,
                }),
            },
        ];

        if (bookingId) {
            try {
                message.loading({
                    key: "sendMail",
                    content: i18n.containers.bookings.BookingSummary.sendBookingConfirmation.messages.loadingText({
                        bookingOrWaitlistConfirmationText,
                    }),
                    duration: 0,
                });

                await sendBookingConfirmation({
                    variables: {
                        bookingId,
                        editedTemplateInput,
                    },
                }).then((res) => {
                    const sentMailResult: SentMailsResult = res.data?.sendBookingConfirmation;

                    if (sentMailResult) {
                        const mailCount = sentMailResult.sentMailCount;
                        const emailText = mailCount === 1 ? "E-Mail" : "E-Mails";
                        const receivers = sentMailResult.sentMessages.map((msg) => msg.To[0].Email);
                        const receiverString = receivers.join(", ");

                        message.success({
                            key: "sendMail",
                            content: `${mailCount} ${emailText} erfolgreich versendet. Empfänger: ${receiverString}`,
                        });
                    }
                });
            } catch (error) {
                handleError("sendBookingConfirmationError", error, "message", knownErrors);
            }
        }
    };

    const handleConfirmAction = async (confirmed: boolean, editedTemplateInput?: EditTemplateInput) => {
        setShowEditor(false);
        if (confirmed) {
            await resendBookingConfirmation(editedTemplateInput);
        }
    };

    return (
        <>
            <MailTemplateEditor
                asModal
                bookingId={booking.id}
                open={showEditor}
                setVisibility={setShowEditor}
                templateType={isWaitlist ? TemplateType.WaitingListConfirmation : TemplateType.BookingConfirmation}
                useCase="SENDMAIL"
                confirmAction={handleConfirmAction}
                receivers={[bookerEmail]}
                hideNotSendButton
            />
            <Tooltip
                title={
                    bookerMailIsMissing
                        ? i18n.containers.bookings.BookingSummary.sendBookingConfirmation.toolTipNoEmailText({
                              bookingOrWaitlistConfirmationText,
                          })
                        : i18n.containers.bookings.BookingSummary.sendBookingConfirmation.toolTipText({
                              bookingOrWaitlistConfirmationText,
                          })
                }
                placement="left"
            >
                <Button {...buttonProps} loading={sendBookingConfirmationLoading} icon={<SendOutlined />} disabled={bookerMailIsMissing} onClick={() => setShowEditor(true)}>
                    {bookingOrWaitlistConfirmationText}
                </Button>
            </Tooltip>
        </>
    );
};

// SEND BLANK MAIL --------------------------------------------------------------------------------------------------

type SendBlankMailProps = {
    // booking: GetBookingByIdQuery["bookingById"];
    booking: BookingType;
    booker: string;
};

export const SendBlankMail: FC<SendBlankMailProps> = ({booking, booker}) => {
    const [open, setOpen] = useState(false);
    const bookerMailIsMissing = !isDefined(booking.attendee?.booker?.email);
    const buttonData = {
        label: "Vorlage wählen",
        icon: <SendOutlined />,
        onClick: () => sendBlankMail(),
    };
    const {label} = buttonData;
    const notImplementedText = `${label} Ist derzeit noch nicht implementiert`;
    const sendBlankMail = () => {
        message.warn(notImplementedText);
    };

    const selectedBooking = booking as SelectionType;

    return (
        <Tooltip title="Eine Vorlage verwenden um den Bucher zu benachrichtigen." placement="left">
            <EmailTemplatePicker
                open={open}
                setOpen={(open) => setOpen(open)}
                mailDataType={MailDataType.Booking}
                selection={[selectedBooking]}
                customButtonContent={
                    <Button {...buttonProps} disabled={bookerMailIsMissing} onClick={() => setOpen(!open)}>
                        <Space>
                            <SendOutlined />
                            {label}
                        </Space>
                    </Button>
                }
            />
        </Tooltip>
    );
};
